import { QueryCache, QueryClient, hashKey } from '@tanstack/react-query';
import { isApiFailure, notifyError } from './errors';

// One cache for every page's server data (TanStack Query).
//
// - A page you already opened shows its saved data instantly and makes no API
//   call for STALE_TIME, so moving between pages doesn't reload everything.
// - Any successful write to our API (POST/PUT/PATCH/DELETE) marks every saved
//   page stale (installWriteInvalidation below): the next visit refetches, so
//   your own changes always show. Pages still refetch their own data right
//   after a write, as they did before.
// - Every entry is scoped to the current login token, so signing in as someone
//   else, switching institution or entering surrogate mode never shows the
//   previous session's data. Sign-out also drops the whole cache.

const STALE_TIME = 5 * 60 * 1000;
const GC_TIME = 30 * 60 * 1000;

const sessionToken = () => {
  try { return localStorage.getItem('desky_token') || ''; } catch { return ''; }
};

export const queryClient = new QueryClient({
  // A failed load gets the same friendly toast as before; a page sets
  // `meta.errorMessage` for its own wording, or `meta.silent` to skip it.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.silent || !isApiFailure(error)) return;
      notifyError(error, query.meta?.errorMessage);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME,
      gcTime: GC_TIME,
      refetchOnWindowFocus: false,
      // Pages already report failures once (toast / inline error); a silent
      // retry would only delay that.
      retry: false,
      queryKeyHashFn: (queryKey) => hashKey([sessionToken(), ...queryKey]),
    },
  },
});

/** Mark every saved page stale without refetching now; each refetches on its next visit. */
export const markAllStale = () => queryClient.invalidateQueries({ refetchType: 'none' });

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
// Background writes the student never sees as an edit (presence pings, view
// counters); letting them mark the cache stale would empty it constantly.
const IGNORED_WRITES = [/\/badges\/heartbeat\b/, /\/badges\/event\b/];

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api/`;

export function isCacheBustingWrite(method, url) {
  if (!WRITE_METHODS.has(String(method || 'GET').toUpperCase())) return false;
  const u = String(url || '');
  if (!(u.startsWith(API_ROOT) || u.startsWith('/api/'))) return false;
  return !IGNORED_WRITES.some(re => re.test(u));
}

/** Install once at startup: successful API writes (fetch or axios) mark the cache stale. */
export function installWriteInvalidation(axios) {
  if (window.__deskeyWriteInvalidation) return;
  window.__deskeyWriteInvalidation = true;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const res = await nativeFetch(input, init);
    const url = typeof input === 'string' ? input : (input?.url || input?.href);
    const method = init?.method || (typeof input === 'object' ? input?.method : undefined);
    if (res.ok && isCacheBustingWrite(method, url)) markAllStale();
    return res;
  };

  axios.interceptors.response.use((response) => {
    const { method, url, baseURL } = response.config || {};
    const full = url && !/^https?:/.test(url) && baseURL ? `${baseURL.replace(/\/$/, '')}/${url.replace(/^\//, '')}` : url;
    if (isCacheBustingWrite(method, full)) markAllStale();
    return response;
  });
}
